import { ReactNode, useId } from 'react';

// Icons from the Rive Editor's icon set (rive packages/editor_icons), on its
// 15px grid. Their 1px lines are strokes clipped to the shape, so each needs
// unique clip ids.
type EditorIcon = { viewBox: string; body: (id: string) => ReactNode };

const editorIcons = {
  bell: {
    viewBox: '0 0 15 15',
    body: (id) => (
      <>
        <defs>
          <clipPath id={`${id}-c1`}>
            <path d="M14.8 11.101C13.656 10.242 13 8.929 13 7.5L13 5.5C13 1.756 9.95 0 7.5 0C5.05 0 2 1.28 2 5.5L2 7.5C2 8.929 1.344 10.241 0.2 11.101C0.028 11.23 -0.042 11.455 0.026 11.659C0.094 11.863 0.285 12.001 0.5 12.001C6.046 12.001 8.071 12.001 14.499 12.001C14.714 12.001 14.905 11.863 14.973 11.659C15.041 11.455 14.972 11.23 14.8 11.101Z" />
          </clipPath>
          <clipPath id={`${id}-c2`}>
            <path d="M5 12.333C5 11.597 5.597 11 6.333 11L8.667 11C9.403 11 10 11.597 10 12.333L10 12.5C10 13.881 8.881 15 7.5 15C6.119 15 5 13.881 5 12.5L5 12.333Z" />
          </clipPath>
        </defs>
        <path
          d="M14.8 11.101C13.656 10.242 13 8.929 13 7.5L13 5.5C13 1.756 9.95 0 7.5 0C5.05 0 2 1.28 2 5.5L2 7.5C2 8.929 1.344 10.241 0.2 11.101C0.028 11.23 -0.042 11.455 0.026 11.659C0.094 11.863 0.285 12.001 0.5 12.001C6.046 12.001 8.071 12.001 14.499 12.001C14.714 12.001 14.905 11.863 14.973 11.659C15.041 11.455 14.972 11.23 14.8 11.101Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c1)`}
        />
        <path
          d="M5 12.333C5 11.597 5.597 11 6.333 11L8.667 11C9.403 11 10 11.597 10 12.333L10 12.5C10 13.881 8.881 15 7.5 15C6.119 15 5 13.881 5 12.5L5 12.333Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c2)`}
        />
      </>
    ),
  },
  link: {
    viewBox: '0 0 15 15',
    body: () => (
      <>
        <path
          d="M9 5.5C9 5.776 9.224 6 9.5 6C9.776 6 10 5.776 10 5.5L9.5 5.5L9 5.5ZM5 5.5C5 5.776 5.224 6 5.5 6C5.776 6 6 5.776 6 5.5L5.5 5.5L5 5.5ZM9.5 3.5L9 3.5L9 5.5L9.5 5.5L10 5.5L10 3.5L9.5 3.5ZM5.5 5.5L6 5.5L6 3.5L5.5 3.5L5 3.5L5 5.5L5.5 5.5ZM5.5 3.5L6 3.5C6 2.672 6.672 2 7.5 2L7.5 1.5L7.5 1C6.119 1 5 2.119 5 3.5L5.5 3.5ZM9.5 3.5L10 3.5C10 2.119 8.881 1 7.5 1L7.5 1.5L7.5 2C8.328 2 9 2.672 9 3.5L9.5 3.5Z"
          fill="currentColor"
        />
        <path
          d="M10 9.5C10 9.224 9.776 9 9.5 9C9.224 9 9 9.224 9 9.5L9.5 9.5L10 9.5ZM6 9.5C6 9.224 5.776 9 5.5 9C5.224 9 5 9.224 5 9.5L5.5 9.5L6 9.5ZM9.5 9.5L9 9.5L9 11.5L9.5 11.5L10 11.5L10 9.5L9.5 9.5ZM5.5 11.5L6 11.5L6 9.5L5.5 9.5L5 9.5L5 11.5L5.5 11.5ZM7.5 13.5L7.5 13C6.672 13 6 12.328 6 11.5L5.5 11.5L5 11.5C5 12.881 6.119 14 7.5 14L7.5 13.5ZM9.5 11.5L9 11.5C9 12.328 8.328 13 7.5 13L7.5 13.5L7.5 14C8.881 14 10 12.881 10 11.5L9.5 11.5Z"
          fill="currentColor"
        />
        <path
          d="M8 4.5C8 4.224 7.776 4 7.5 4C7.224 4 7 4.224 7 4.5L7.5 4.5L8 4.5ZM7 10.5C7 10.776 7.224 11 7.5 11C7.776 11 8 10.776 8 10.5L7.5 10.5L7 10.5ZM7.5 4.5L7 4.5L7 10.5L7.5 10.5L8 10.5L8 4.5L7.5 4.5Z"
          fill="currentColor"
        />
      </>
    ),
  },
  general: {
    viewBox: '0 0 15 15',
    body: (id) => (
      <>
        <defs>
          <clipPath id={`${id}-c1`}>
            <path d="M9 0L9 1.465C9 2.355 10.077 2.801 10.707 2.172L11.742 1.136L13.864 3.257L12.828 4.293C12.198 4.923 12.645 6 13.535 6L15 6L15 9L14.743 9C13.407 9 12.737 10.616 13.682 11.56L13.864 11.742L11.742 13.864L10.707 12.828C10.077 12.198 9 12.645 9 13.535L9 15L6 15L6 13.535C6 12.645 4.923 12.198 4.293 12.828L3.258 13.864L1.136 11.742L2.172 10.707C2.801 10.077 2.355 9 1.465 9L0 9L0 6L1.465 6C2.355 6 2.802 4.923 2.172 4.293L1.136 3.257L3.258 1.136L4.293 2.172C4.923 2.801 6 2.355 6 1.465L6 0L9 0ZM7.5 6C6.672 6 6 6.672 6 7.5C6 8.328 6.672 9 7.5 9C8.328 9 9 8.328 9 7.5C9 6.672 8.328 6 7.5 6Z" />
          </clipPath>
        </defs>
        <path
          d="M9 0L9 1.465C9 2.355 10.077 2.801 10.707 2.172L11.742 1.136L13.864 3.257L12.828 4.293C12.198 4.923 12.645 6 13.535 6L15 6L15 9L14.743 9C13.407 9 12.737 10.616 13.682 11.56L13.864 11.742L11.742 13.864L10.707 12.828C10.077 12.198 9 12.645 9 13.535L9 15L6 15L6 13.535C6 12.645 4.923 12.198 4.293 12.828L3.258 13.864L1.136 11.742L2.172 10.707C2.801 10.077 2.355 9 1.465 9L0 9L0 6L1.465 6C2.355 6 2.802 4.923 2.172 4.293L1.136 3.257L3.258 1.136L4.293 2.172C4.923 2.801 6 2.355 6 1.465L6 0L9 0ZM7.5 6C6.672 6 6 6.672 6 7.5C6 8.328 6.672 9 7.5 9C8.328 9 9 8.328 9 7.5C9 6.672 8.328 6 7.5 6Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          clipPath={`url(#${id}-c1)`}
        />
      </>
    ),
  },
  members: {
    viewBox: '0 0 15 15',
    body: (id) => (
      <>
        <defs>
          <clipPath id={`${id}-c1`}>
            <path d="M7 4.5C7 5.881 5.881 7 4.5 7C3.119 7 2 5.881 2 4.5C2 3.119 3.119 2 4.5 2C5.881 2 7 3.119 7 4.5Z" />
          </clipPath>
          <clipPath id={`${id}-c2`}>
            <path d="M13 5C13 6.105 12.105 7 11 7C9.895 7 9 6.105 9 5C9 3.895 9.895 3 11 3C12.105 3 13 3.895 13 5Z" />
          </clipPath>
          <clipPath id={`${id}-c3`}>
            <path d="M9 12L9 13L0 13L0 12C0 9.791 2.015 8 4.5 8C6.985 8 9 9.791 9 12Z" />
          </clipPath>
        </defs>
        <path
          d="M7 4.5C7 5.881 5.881 7 4.5 7C3.119 7 2 5.881 2 4.5C2 3.119 3.119 2 4.5 2C5.881 2 7 3.119 7 4.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c1)`}
        />
        <path
          d="M13 5C13 6.105 12.105 7 11 7C9.895 7 9 6.105 9 5C9 3.895 9.895 3 11 3C12.105 3 13 3.895 13 5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c2)`}
        />
        <path
          d="M9 12L9 13L0 13L0 12C0 9.791 2.015 8 4.5 8C6.985 8 9 9.791 9 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c3)`}
        />
        <path
          d="M11 8C13.209 8 15 9.791 15 12L15 13L10 13L10 12L14 12C14 10.343 12.657 9 11 9C10.383 9 9.809 9.186 9.332 9.506C9.157 9.225 8.949 8.961 8.715 8.718C9.363 8.266 10.15 8 11 8Z"
          fill="currentColor"
        />
      </>
    ),
  },
  billing: {
    viewBox: '0 -3 20 20',
    body: () => (
      <>
        <path
          d="M18.5 0L1.5 0C0.673 0 0 0.673 0 1.5L0 12.5C0 13.327 0.673 14 1.5 14L18.5 14C19.327 14 20 13.327 20 12.5L20 1.5C20 0.673 19.327 0 18.5 0ZM1.5 1L18.5 1C18.776 1 19 1.224 19 1.5L19 2L1 2L1 1.5C1 1.224 1.224 1 1.5 1ZM19 3L19 6L1 6L1 3L19 3ZM18.5 13L1.5 13C1.224 13 1 12.776 1 12.5L1 7L19 7L19 12.5C19 12.776 18.776 13 18.5 13Z"
          fill="currentColor"
        />
      </>
    ),
  },
  lock: {
    viewBox: '0 0 15 15',
    body: () => (
      <>
        <path
          d="M5.379 3.379L5.025 3.025L5.025 3.025L5.379 3.379ZM9.621 3.379L9.975 3.025L9.975 3.025L9.621 3.379ZM4 6.5C4 6.776 4.224 7 4.5 7C4.776 7 5 6.776 5 6.5L4.5 6.5L4 6.5ZM10 6.5C10 6.776 10.224 7 10.5 7C10.776 7 11 6.776 11 6.5L10.5 6.5L10 6.5ZM4.5 5.5L5 5.5C5 4.837 5.263 4.201 5.732 3.732L5.379 3.379L5.025 3.025C4.369 3.681 4 4.572 4 5.5L4.5 5.5ZM5.379 3.379L5.732 3.732C6.201 3.263 6.837 3 7.5 3L7.5 2.5L7.5 2C6.572 2 5.681 2.369 5.025 3.025L5.379 3.379ZM7.5 2.5L7.5 3C8.163 3 8.799 3.263 9.268 3.732L9.621 3.379L9.975 3.025C9.319 2.369 8.428 2 7.5 2L7.5 2.5ZM9.621 3.379L9.268 3.732C9.737 4.201 10 4.837 10 5.5L10.5 5.5L11 5.5C11 4.572 10.631 3.681 9.975 3.025L9.621 3.379ZM4.5 5.5L4 5.5L4 6.5L4.5 6.5L5 6.5L5 5.5L4.5 5.5ZM10.5 5.5L10 5.5L10 6.5L10.5 6.5L11 6.5L11 5.5L10.5 5.5Z"
          fill="currentColor"
        />
        <path
          d="M3.5 7C3.5 6.724 3.724 6.5 4 6.5L11 6.5C11.276 6.5 11.5 6.724 11.5 7L11.5 12C11.5 12.276 11.276 12.5 11 12.5L4 12.5C3.724 12.5 3.5 12.276 3.5 12L3.5 7Z"
          fill="currentColor"
          fillOpacity="0.25"
        />
        <path
          d="M4 6.5L4 7L11 7L11 6.5L11 6L4 6L4 6.5ZM11.5 7L11 7L11 12L11.5 12L12 12L12 7L11.5 7ZM11 12.5L11 12L4 12L4 12.5L4 13L11 13L11 12.5ZM3.5 12L4 12L4 7L3.5 7L3 7L3 12L3.5 12ZM4 12.5L4 12L4 12L3.5 12L3 12C3 12.552 3.448 13 4 13L4 12.5ZM11.5 12L11 12L11 12L11 12.5L11 13C11.552 13 12 12.552 12 12L11.5 12ZM11 6.5L11 7L11 7L11.5 7L12 7C12 6.448 11.552 6 11 6L11 6.5ZM4 6.5L4 6C3.448 6 3 6.448 3 7L3.5 7L4 7L4 7L4 6.5Z"
          fill="currentColor"
        />
      </>
    ),
  },
  folder: {
    viewBox: '0 0 15 15',
    body: (id) => (
      <>
        <defs>
          <clipPath id={`${id}-c1`}>
            <path
              d="M2.5 13C1.673 13 1 12.327 1 11.5L1 5.001C1 4.649 1.119 4.145 1.276 3.83L1.829 2.724C2.035 2.311 2.539 2 3 2L7 2C7.461 2 7.965 2.311 8.171 2.724L8.724 3.83C8.762 3.907 8.915 4.001 9 4.001L12.5 4.001C13.327 4.001 14 4.674 14 5.501L14 11.5C14 12.327 13.327 13 12.5 13L2.5 13Z"
              clipRule="evenodd"
            />
          </clipPath>
        </defs>
        <path
          d="M2.5 13C1.673 13 1 12.327 1 11.5L1 5.001C1 4.649 1.119 4.145 1.276 3.83L1.829 2.724C2.035 2.311 2.539 2 3 2L7 2C7.461 2 7.965 2.311 8.171 2.724L8.724 3.83C8.762 3.907 8.915 4.001 9 4.001L12.5 4.001C13.327 4.001 14 4.674 14 5.501L14 11.5C14 12.327 13.327 13 12.5 13L2.5 13Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c1)`}
        />
      </>
    ),
  },
  globe: {
    viewBox: '0 0 15 15',
    body: (id) => (
      <>
        <defs>
          <clipPath id={`${id}-c1`}>
            <path d="M15 7.5C15 11.642 11.642 15 7.5 15C3.358 15 0 11.642 0 7.5C0 3.358 3.358 0 7.5 0C11.642 0 15 3.358 15 7.5Z" />
          </clipPath>
          <clipPath id={`${id}-c2`}>
            <path d="M12 7.5C12 11.642 9.985 15 7.5 15C5.015 15 3 11.642 3 7.5C3 3.358 5.015 0 7.5 0C9.985 0 12 3.358 12 7.5Z" />
          </clipPath>
        </defs>
        <path
          d="M15 7.5C15 11.642 11.642 15 7.5 15C3.358 15 0 11.642 0 7.5C0 3.358 3.358 0 7.5 0C11.642 0 15 3.358 15 7.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c1)`}
        />
        <path
          d="M12 7.5C12 11.642 9.985 15 7.5 15C5.015 15 3 11.642 3 7.5C3 3.358 5.015 0 7.5 0C9.985 0 12 3.358 12 7.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          clipPath={`url(#${id}-c2)`}
        />
        <path d="M7.5 0L7 0L7 15L7.5 15L8 15L8 0L7.5 0Z" fill="currentColor" />
        <path d="M0 7.5L0 8L15 8L15 7.5L15 7L0 7L0 7.5Z" fill="currentColor" />
        <path d="M1 4.5L1 5L14 5L14 4.5L14 4L1 4L1 4.5Z" fill="currentColor" />
        <path
          d="M1 10.5L1 11L14 11L14 10.5L14 10L1 10L1 10.5Z"
          fill="currentColor"
        />
      </>
    ),
  },
} satisfies Record<string, EditorIcon>;

// Drawn for the account page mockup, not from the editor set.
const paths = {
  credits: 'M12 3v18M17 7H9.5a3 3 0 0 0 0 6h5a3 3 0 0 1 0 6H6',
  sso: 'M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6z',
  key: 'M10 14a4 4 0 1 1 2.8-1.2L20 20M17 17l2-2',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0zM12 17v4',
  user: 'M20 20v-1a5 5 0 0 0-5-5H9a5 5 0 0 0-5 5v1M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  shield: 'M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6zM9 12l2 2 4-4',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  chevronUpDown: 'M8 9l4-4 4 4M8 15l4 4 4-4',
  chevronDown: 'M6 9l6 6 6-6',
  chevronLeft: 'M15 18l-6-6 6-6',
  chevronRight: 'M9 6l6 6-6 6',
  check: 'M5 12l5 5 9-10',
  plus: 'M12 5v14M5 12h14',
  close: 'M6 6l12 12M18 6L6 18',
  external: 'M7 17L17 7M9 7h8v8',
  menu: 'M4 7h16M4 12h16M4 17h16',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  warning: 'M12 3l10 18H2zM12 10v5M12 18v.01',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 11v6M12 7.5v.5',
  chat: 'M4 5h16v11H9l-5 4z',
  doc: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h7',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M20 20l-4-4',
  signOut: 'M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10',
};

type EditorIconName = keyof typeof editorIcons;

export type AccountIconName = keyof typeof paths | EditorIconName;

export function AccountIcon({
  name,
  size = 16,
  color = 'currentColor',
  strokeWidth = 1.8,
}: {
  name: AccountIconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
}) {
  // useId() contains characters that break url(#…) references.
  const id = useId().replace(/[^\w-]/g, '');
  if (name in editorIcons) {
    const editorIcon: EditorIcon = editorIcons[name as EditorIconName];
    return (
      <svg
        width={size}
        height={size}
        viewBox={editorIcon.viewBox}
        fill="none"
        color={color}
        aria-hidden="true"
        focusable="false"
      >
        {editorIcon.body(id)}
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={paths[name as keyof typeof paths]} />
    </svg>
  );
}
